"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Compass,
  Sparkles,
  Brain,
  Target,
  Stethoscope,
  ShieldCheck,
  Activity,
  Lock,
  Heart,
  Eye,
  Vote,
  Building2,
  Flame,
  Rocket,
  Cpu,
  HeartPulse,
  Code2,
  Globe2,
  Crown,
  Briefcase,
  Zap,
  BookOpen,
  ArrowRight,
  Layers,
  Award,
  Mic,
  GraduationCap,
  TrendingUp,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const HEADLINE_STATS = [
  { metric: "15", suffix: "/18", label: "Campaign Victories", sub: "Across sovereign-scale engagements", accent: "red" },
  { metric: "900", suffix: "M+", label: "Voters Reached", sub: "Across multiple languages", accent: "purple" },
  { metric: "300", suffix: "+", label: "Elite Clients Served", sub: "Nine client categories", accent: "cyan" },
  { metric: "0", suffix: "", label: "Security Incidents", sub: "15+ years clean record", accent: "emerald" },
  { metric: "18", suffix: "", label: "Countries", sub: "Three continents", accent: "amber-gold" },
  { metric: "15", suffix: "", label: "Expertise Domains", sub: "One integrated operating system", accent: "denim" },
];

const DOMAINS = [
  { icon: Brain, title: "Operational Excellence", href: "/expertise/operational-excellence/", body: "The engine that produces verified outcomes. Protocols, checklists, reviews, outcome accountability.", accent: "red" },
  { icon: Eye, title: "Strategic Intelligence", href: "/expertise/strategic-intelligence/", body: "Seeing what others cannot. The early-warning system that converts data into decision-ready intelligence.", accent: "purple" },
  { icon: Vote, title: "Political Strategy", href: "/expertise/political-strategy/", body: "Engineering electoral outcomes. Booth-level intelligence and verified campaign victories.", accent: "cyan" },
  { icon: Building2, title: "Governance & Public Policy", href: "/expertise/governance-public-policy/", body: "Converting authority into outcomes. eGovernance, citizen engagement, policy implementation.", accent: "emerald" },
  { icon: Flame, title: "Crisis Management", href: "/expertise/crisis-management/", body: "The crisis transformation engine. Compressing damage windows and recovering in 30-60 days.", accent: "amber-gold" },
  { icon: Lock, title: "Privacy & Information Security", href: "/expertise/privacy-information-security/", body: "Sovereign-grade protection. Military-grade encryption, zero trust, zero breaches.", accent: "denim" },
  { icon: Heart, title: "Women in Leadership", href: "/expertise/women-in-leadership/", body: "The operating case for the talent the world overlooks. A decade of verified evidence.", accent: "coral" },
  { icon: TrendingUp, title: "Perception & Reputation", href: "/expertise/perception-reputation/", body: "Engineering how the world sees you. Narrative engineering at continental scale.", accent: "lime" },
  { icon: Stethoscope, title: "Leadership as Diagnosis", href: "/expertise/leadership-as-diagnosis/", body: "The clinical method applied to organizations. Six principles forged in a delivery room.", accent: "red" },
  { icon: Rocket, title: "Entrepreneurship & Building", href: "/expertise/entrepreneurship/", body: "The discipline of constructing consequential organizations that outlast their founders.", accent: "purple" },
  { icon: Cpu, title: "AI, Technology & The Future", href: "/expertise/ai-technology-future/", body: "Governing intelligence at sovereign scale. The AI architecture the future demands.", accent: "cyan" },
  { icon: HeartPulse, title: "Medicine Meets Technology", href: "/expertise/medicine-meets-technology/", body: "The physician's discipline for the digital age. Clinical rigor applied to sovereign systems.", accent: "emerald" },
  { icon: Code2, title: "Digital Transformation", href: "/expertise/digital-transformation/", body: "Engineering the digital future. The operating system for statecraft at national scale.", accent: "amber-gold" },
  { icon: Globe2, title: "Global Operations", href: "/expertise/global-operations/", body: "Sovereign-scale execution across continents. Eighteen countries, one unified command.", accent: "denim" },
  { icon: Sparkles, title: "Women's Empowerment", href: "/expertise/womens-empowerment/", body: "The operating case, the evidence, the movement. The 70% Project as flagship initiative.", accent: "coral" },
];

const CLINICAL_PRINCIPLES = [
  { num: "01", title: "Diagnose Before Treating", body: "The first rule of medicine and the most violated rule of leadership. Understand the condition before acting on it. The symptom is never the disease.", accent: "red" },
  { num: "02", title: "Triage Under Pressure", body: "When everything is urgent, nothing is solved well. The leaders who fail rarely lack the ability to act. They lack the ability to rank.", accent: "purple" },
  { num: "03", title: "Run the Protocol; Keep the Judgment", body: "Protocols carry through the ninety-five percent. Judgment handles the five percent that do not. Brilliance without a system is unsustainable.", accent: "cyan" },
  { num: "04", title: "Own the Outcome, Not the Effort", body: "Medicine does not credit a physician for how hard the attempt was. The patient lived or did not. The organization is measured by what it delivered.", accent: "emerald" },
  { num: "05", title: "First, Do No Harm", body: "Under pressure, most leaders make the situation worse. The discipline of not making it worse is the most underrated operational skill.", accent: "amber-gold" },
  { num: "06", title: "Hire the Talent Others Overlook", body: "The most reliable competitive advantage available is the one the world is mis-pricing. Optimize for the right signals and the talent emerges.", accent: "denim" },
];

const METHODOLOGY_STAGES = [
  { num: "01", title: "Intelligence", body: "Every engagement begins with intelligence, not assumptions. The ground truth is discovered before any strategy is formed." },
  { num: "02", title: "Analysis", body: "Pattern identification, anomaly detection, causal analysis. The symptom is never the disease. The analysis is forensic, not descriptive." },
  { num: "03", title: "Strategy", body: "The Penta P framework is deployed. Each pillar assigned its role. Strategy is not a document. It is an operational plan driving coordinated action." },
  { num: "04", title: "Orchestration", body: "The neural command interface coordinates every platform simultaneously, reducing decision time from days to under one hour." },
  { num: "05", title: "Amplification", body: "Content deploys at scale through platform-optimized channels with organic authenticity. The signal becomes louder than the noise." },
  { num: "06", title: "Feedback", body: "Every engagement makes the next one smarter. The system that enters the next engagement is more precise than the one that entered the last." },
];

const PILLARS = [
  { title: "Perception", body: "Narrative engineering, reputation management, perception proliferation. Shaping how the world sees a leader, institution, or nation.", accent: "red" },
  { title: "Privacy", body: "Sovereign-grade encryption, zero trust, post-quantum cryptography. The difference between operational survival and catastrophe.", accent: "purple" },
  { title: "Politics", body: "Electoral intelligence, voter analytics, campaign engineering. Booth-level precision that has produced verified victories.", accent: "cyan" },
  { title: "Policing", body: "Law enforcement intelligence, national security frameworks, threat operations. The defense layer of strategic sovereignty.", accent: "emerald" },
  { title: "Policy", body: "eGovernance transformation, citizen engagement, policy implementation. Converting political authority into measurable public outcomes.", accent: "amber-gold" },
];

const AUDIENCES = [
  { icon: Crown, title: "Governments & Heads of State", body: "Operational excellence, governance transformation, and strategic sovereignty when political authority must convert into public outcomes.", accent: "red" },
  { icon: ShieldCheck, title: "Royal Families & Dynasties", body: "Reputation protection, privacy enforcement, and legacy narrative management with national security level confidentiality.", accent: "purple" },
  { icon: Target, title: "Political Leaders & Organizations", body: "Voter intelligence, campaign engineering, and verified electoral outcomes where winning is not optional.", accent: "cyan" },
  { icon: Award, title: "Defense & Intelligence Agencies", body: "Secure communications, threat detection, intelligence fusion, and cyber forensics with sovereign-grade discipline.", accent: "emerald" },
  { icon: Briefcase, title: "Fortune-Scale Corporations", body: "Brand protection, crisis response, competitive positioning, and operational transformation at sovereign grade.", accent: "amber-gold" },
  { icon: Heart, title: "Aspiring Women Leaders", body: "A role model who has built, not just advocated. Evidence that the talent the world overlooks is the talent that outperforms.", accent: "coral" },
];

export default function ExpertiseHubPage() {
  return (
    <>
      <PageHero
        icon={Compass}
        overline="Expertise Hub"
        overlineClassName="text-red"
        title={
          <>
            Fifteen Domains. <span className="text-gradient-premium">One Operating System.</span>
          </>
        }
        subtitle="The comprehensive map of expertise Dr. Jyoti Kush commands as Chief Operating Officer. Each domain is a chapter in a single integrated system — the clinical method applied to sovereign-scale operations across eighteen countries, nine proprietary AI platforms, and sixty-plus integrated services."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Expertise" }]}
        badge="A decade of sovereign-scale delivery"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
              <Sparkles className="size-3.5 text-purple" />
              <span className="text-overline text-purple">Executive Summary</span>
            </div>
            <h2 className="text-display-md mb-5">
              The full spectrum of sovereign-scale expertise — documented, integrated, and proven.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Dr. Jyoti Kush is the Chief Operating Officer of CryptoMize and MaxiMize Infinium. The expertise she commands spans fifteen interconnected domains — from operational excellence and strategic intelligence through governance transformation and crisis management to the operating case for the talent the world overlooks. These are not separate practices. They are one integrated operating system, forged in clinical practice, proven across eighteen countries on three continents, and refined through a decade of sovereign-scale engagements.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The hubs that follow document the full body of expertise. Each domain includes the thesis, the method, the infrastructure, and the evidence. The connection between domains is where the value concentrates — perception informs politics, intelligence drives strategy, governance validates operations, and the clinical discipline underwrites all of it.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The teams built to execute this expertise are, in most offices, over seventy percent women — a finding that emerged not from policy but from performance. The thought leadership — The 70% Project, The Operator&apos;s Notebook, the keynotes at the Nobel Peace Prize and the World Economic Forum — extends the operating case to every organization willing to hire against the right signals.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {HEADLINE_STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${ACCENT[s.accent].bg} to-transparent border ${ACCENT[s.accent].border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-sm font-bold ${ACCENT[s.accent].text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Fifteen Expertise Domains</span>
          </div>
          <h2 className="text-display-md mb-4">The complete map of expertise.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each domain is a chapter in a single integrated system. The connection between domains is where the value concentrates — and where conventional single-domain specialists cannot compete.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {DOMAINS.map((d, i) => {
            const a = ACCENT[d.accent];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
              >
                <Link href={d.href} className="block h-full group">
                  <div className={`relative h-full glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}>
                    <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                    <div className="relative">
                      <div className="size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center mb-3 shadow-lg">
                        <d.icon className="size-5 text-white" />
                      </div>
                      <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{d.title}</h3>
                      <p className="text-caption text-muted-foreground leading-relaxed mb-3">{d.body}</p>
                      <div className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                        Explore <ArrowRight className="size-3" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Stethoscope className="size-3.5 text-red" />
            <span className="text-overline text-red">The Operating Method</span>
          </div>
          <h2 className="text-display-md mb-4">Six clinical principles. One doctrine.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The clinical method — forged in a delivery room, proven across eighteen countries — is the operating system that governs every engagement. These are not leadership metaphors. They are the actual principles that produce verified outcomes at sovereign scale.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CLINICAL_PRINCIPLES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{p.num}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{p.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-5`}>
              <Activity className="size-3.5 text-amber-gold" />
              <span className="text-overline text-amber-gold">The Six-Stage Methodology</span>
            </div>
            <h2 className="text-display-md mb-5">The battle-tested operating system.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Every engagement — regardless of domain, jurisdiction, or stakes — follows the same six-stage operating system refined over hundreds of sovereign-scale deployments. The stages are sequential in logic but iterative in practice. Intelligence flows continuously through all stages. Feedback from later stages refines earlier ones.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The methodology closes a loop that never breaks. The system that enters the next engagement is smarter than the one that entered the last. Every outcome — win or loss — adds to the training corpus. Every cycle produces more precise intelligence than the one before.
            </p>
          </div>
          <div className="space-y-3">
            {METHODOLOGY_STAGES.map((s, i) => {
              const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim"];
              const a = ACCENT[accents[i]];
              return (
                <motion.div
                  key={s.num}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg ring-2 ${a.ring}`}>
                      <span className="text-body-md font-bold text-white">{s.num}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{s.title}</h3>
                      <p className="text-body-sm text-muted-foreground leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Zap className="size-3.5 text-coral" />
            <span className="text-overline text-coral">The Penta P Framework</span>
          </div>
          <h2 className="text-display-md mb-4">Five interconnected pillars. One closed-loop system.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The framework that organizes sixty-plus integrated services into a coherent architecture. Perception informs Politics informs Policy informs Policing informs Privacy — and the loop never breaks. The integration is the moat. The orchestration is the advantage.
          </p>
        </div>
        <div className="grid md:grid-cols-5 gap-3">
          {PILLARS.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`relative glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="text-display-sm font-bold text-foreground/30 leading-none mb-3">{`0${i + 1}`}</div>
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <Heart className="size-3.5 text-lime" />
            <span className="text-overline text-lime">The 70% Operating Model</span>
          </div>
          <h2 className="text-display-md mb-4">People as the foundation of every system.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The teams built across eighteen countries are, in most offices, over seventy percent women. This is not a diversity initiative. It is an operating finding — a decade of evidence that the talent the world overlooks is the talent that outperforms. The argument is from operations, not morality.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { num: "01", title: "Communication", body: "Problems surface earlier because the team is willing to say the uncomfortable thing before it becomes a catastrophe." },
            { num: "02", title: "Risk Discipline", body: "Protocol adherence without sacrificing judgment. The 99.9999% uptime standard is the product of this discipline." },
            { num: "03", title: "Collaboration", body: "Internal energy goes outward, toward the mission, instead of inward, toward each other. Coordination efficiency rises." },
            { num: "04", title: "Retention", body: "Institutional knowledge preserved because the environment is one where people can stay and thrive." },
            { num: "05", title: "Ego Management", body: "Ego interferes less with execution. The systems function as designed, without distortions." },
          ].map((t, i) => (
            <motion.div
              key={t.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="relative glass-card rounded-2xl p-5 bg-gradient-to-br from-lime/10 to-transparent border border-lime/20"
            >
              <div className="text-display-sm font-bold text-lime/30 leading-none mb-3">{t.num}</div>
              <h3 className="text-heading-md mb-2 text-lime">{t.title}</h3>
              <p className="text-caption text-muted-foreground leading-relaxed">{t.body}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link
            href="/the-70-percent-project/"
            className="inline-flex items-center gap-2 text-caption font-semibold text-lime hover:gap-3 transition-all"
          >
            Explore the 70% Project <ArrowRight className="size-3.5" />
          </Link>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Briefcase className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Who This Serves</span>
          </div>
          <h2 className="text-display-md mb-4">Six categories of engagement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The people and institutions that engage this expertise — from sovereign-level clients to aspiring leaders seeking a role model who has built, not just advocated.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {AUDIENCES.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <a.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${ac.text}`}>{a.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{a.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <BookOpen className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Sovereign-Scale Evidence</span>
          </div>
          <h2 className="text-display-md mb-4">The verified track record.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every metric traces to operational data. Every claim is grounded in the record. The evidence is not aspirational. It is the verified output of a decade of sovereign-scale operations.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { metric: "83.3", suffix: "%", label: "Campaign Success", sub: "15 victories / 18 engagements", accent: "red" },
            { metric: "900", suffix: "M+", label: "Voters Influenced", sub: "Continental reach", accent: "purple" },
            { metric: "99.9999", suffix: "%", label: "Uptime", sub: "31.5 seconds max/year", accent: "cyan" },
            { metric: "0", suffix: "", label: "Security Incidents", sub: "15+ years clean record", accent: "emerald" },
            { metric: "18", suffix: "", label: "Countries", sub: "Three continents", accent: "amber-gold" },
            { metric: "60+", suffix: "", label: "Integrated Services", sub: "Five pillars", accent: "denim" },
            { metric: "70", suffix: "%+", label: "Women in Teams", sub: "By selection, results", accent: "coral" },
            { metric: "100", suffix: "%", label: "Client Satisfaction", sub: "Every engagement", accent: "lime" },
          ].map((e, i) => {
            const a = ACCENT[e.accent];
            return (
              <motion.div
                key={e.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} text-center`}
              >
                <div className={`text-display-md font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                <div className="text-caption text-muted-foreground">{e.sub}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-red mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage the expertise.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Speaking engagements, executive advisory, board service, operational consulting, and sovereign-grade engagements. Every engagement governed by the six-stage methodology. Every outcome verified. National security level confidentiality from first contact.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/about/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <GraduationCap className="size-4" />
                About Dr. Jyoti Kush
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Crown, accent: "red" },
          { title: "The 70% Project", description: "The operating case for the talent the world overlooks. A decade of verified evidence.", href: "/the-70-percent-project/", icon: Heart, accent: "emerald" },
          { title: "The Operator's Notebook", description: "Biweekly notes on how excellent things run. The operating principles distilled.", href: "/the-operators-notebook/", icon: BookOpen, accent: "amber-gold" },
          { title: "Platforms", description: "The proprietary AI ecosystem that powers the expertise. Built, not bought.", href: "/platforms/", icon: Layers, accent: "cyan" },
        ]}
      />
    </>
  );
}
